import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CardArt } from '../components/CardArt.tsx';
import { TopBar } from '../components/Chrome.tsx';
import { SellModal } from '../components/TradeModals.tsx';
import { HttpError, deleteHolding, fx, listHoldings, portfolio } from '../lib/api.ts';
import {
  FX_FALLBACK,
  fmtDateShort,
  fmtHKD,
  fmtPctSigned,
  fmtSignedHKD,
  fmtUSD,
  labelForSignal,
} from '../lib/format.ts';
import type { CardDetail, CardSummary, Holding } from '../lib/types.ts';

type View = 'open' | 'closed';
type Mode = 'real' | 'paper';

export function Holdings() {
  const [view, setView] = useState<View>('open');
  const [mode, setMode] = useState<Mode>('real');
  const [sellTarget, setSellTarget] = useState<Holding | null>(null);

  const fxQ = useQuery({ queryKey: ['fx'], queryFn: fx });
  const fxData = fxQ.data ?? FX_FALLBACK;
  const fxRate = fxData.usd_hkd;

  const portfolioQ = useQuery({
    queryKey: ['portfolio', mode],
    queryFn: () => portfolio(mode),
  });

  const holdingsQ = useQuery({
    queryKey: ['holdings', mode, view],
    queryFn: () => listHoldings(view, mode),
  });

  const p = portfolioQ.data;
  const rows = holdingsQ.data ?? [];

  const totalPos = (p?.totalHkd ?? 0) >= 0;
  const unrealPos = (p?.unrealizedHkd ?? 0) >= 0;
  const realPos = (p?.realizedHkd ?? 0) >= 0;
  const unrealPct = p && p.costBasisOpenHkd > 0 ? (p.unrealizedHkd / p.costBasisOpenHkd) * 100 : 0;

  return (
    <div className={`screen screen-desktop ${mode === 'paper' ? 'port-mode-paper' : ''}`}>
      <TopBar view="holdings" pnlHkd={p?.totalHkd ?? 0} />

      <div className="portfolio-page">
        {/* Summary stat strip */}
        <div className="port-header">
          <div className="port-stat">
            <span className="k">Total P&L · {mode === 'paper' ? 'model' : 'all-time'}</span>
            <span className={`v mono ${p && p.totalHkd === 0 ? '' : totalPos ? 'pos' : 'neg'}`}>
              {p ? fmtSignedHKD(p.totalHkd) : '—'}
            </span>
            <span className="sub">
              {p ? `≈ $${fmtUSD(Math.abs(p.totalHkd) / fxRate)} USD` : ' '}
            </span>
          </div>
          <div className="port-stat">
            <span className="k">Unrealized</span>
            <span
              className={`v mono ${p && p.unrealizedHkd === 0 ? '' : unrealPos ? 'pos' : 'neg'}`}
            >
              {p ? fmtSignedHKD(p.unrealizedHkd) : '—'}
            </span>
            <span className="sub mono">
              {p ? `${fmtPctSigned(unrealPct)} · ${p.openCount} open` : ' '}
            </span>
          </div>
          <div className="port-stat">
            <span className="k">Realized</span>
            <span className={`v mono ${p && p.realizedHkd === 0 ? '' : realPos ? 'pos' : 'neg'}`}>
              {p ? fmtSignedHKD(p.realizedHkd) : '—'}
            </span>
            <span className="sub mono">
              {p
                ? `${p.closedCount} closed${p.winRate != null ? ` · win ${(p.winRate * 100).toFixed(0)}%` : ''}`
                : ' '}
            </span>
          </div>
          <div className="port-stat">
            <span className="k">Cost basis (open)</span>
            <span className="v mono">{p ? `HK$${fmtHKD(p.costBasisOpenHkd)}` : '—'}</span>
            <span className="sub mono">
              {p ? `market HK$${fmtHKD(p.marketValueOpenHkd)}` : ' '}
            </span>
          </div>
          <div className="port-stat">
            <span className="k">Avg hold</span>
            <span className="v mono">
              {p && p.avgHoldDays != null ? `${Math.round(p.avgHoldDays)}d` : '—'}
            </span>
            <span className="sub mono">
              {p?.closedCount ? `${p.closedCount} sold` : 'no closed yet'}
            </span>
          </div>
        </div>

        {mode === 'paper' && (
          <div className="port-paper-banner">
            <span className="paper-tag">paper</span>
            <span>
              Tracking what-if positions. P&L is computed against live prices but no real money is
              involved — use this to test conviction or stress-test the signal.
            </span>
          </div>
        )}

        {/* Tabs + mode toggle */}
        <div className="port-tabs">
          <button
            type="button"
            className={`port-tab ${view === 'open' ? 'on' : ''}`}
            onClick={() => setView('open')}
          >
            Open positions <span className="ck">{p?.openCount ?? 0}</span>
          </button>
          <button
            type="button"
            className={`port-tab ${view === 'closed' ? 'on' : ''}`}
            onClick={() => setView('closed')}
          >
            Closed <span className="ck">{p?.closedCount ?? 0}</span>
          </button>
          <div className="port-mode">
            <button
              type="button"
              className={mode === 'real' ? 'on' : ''}
              onClick={() => setMode('real')}
            >
              Real
            </button>
            <button
              type="button"
              className={mode === 'paper' ? 'on' : ''}
              onClick={() => setMode('paper')}
            >
              Paper
            </button>
          </div>
        </div>

        {/* Table or empty */}
        {holdingsQ.isLoading ? (
          <div style={{ padding: 40, color: 'var(--ink-mute)' }}>loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState view={view} mode={mode} />
        ) : (
          <HoldingsTable rows={rows} view={view} onSell={(h) => setSellTarget(h)} />
        )}
      </div>

      {sellTarget?.card && (
        <SellModal
          card={
            {
              id: sellTarget.cardId,
              productId: sellTarget.cardId,
              jp: sellTarget.card.jp,
              jpEn: sellTarget.card.jpEn,
              set: sellTarget.card.set,
              num: sellTarget.card.num,
              rarity: sellTarget.card.rarity,
              image: sellTarget.card.image,
              signal: sellTarget.card.signal,
              score: sellTarget.card.score,
              pop: null,
              subs: [],
              price: sellTarget.currentHkd != null ? sellTarget.currentHkd / fxRate : null,
              d1: null,
              d7: null,
              d30: null,
              snkrdunkUrl: null,
              detail: {
                trend: 'neutral',
                velocity: 'neutral',
                spread: 'neutral',
                'z-score': 'neutral',
                pop: 'disabled',
              },
              history: [],
              traded: [],
            } as CardDetail
          }
          fx={fxData}
          onClose={() => setSellTarget(null)}
          holdingId={sellTarget.id}
          buyHkd={sellTarget.buyHkd}
          qty={sellTarget.qty}
          daysHeld={daysHeldFor(sellTarget.buyAt)}
        />
      )}
    </div>
  );
}

function daysHeldFor(buyAt: string): number {
  const ms = Date.now() - new Date(buyAt).getTime();
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
}

function EmptyState({ view, mode }: { view: View; mode: Mode }) {
  return (
    <div className="port-empty">
      <div className="empty-card">
        <div className="empty-mark">∅</div>
        <h2>
          No {view} {mode === 'paper' ? 'paper ' : ''}positions yet
        </h2>
        <p>
          {mode === 'paper' ? (
            view === 'open' ? (
              <>
                Open a card from the dashboard and pick <em>Paper</em> in the Buy modal to test a
                what-if without spending money. P&L tracks live prices.
              </>
            ) : (
              "Closed paper positions land here once you 'sell' them."
            )
          ) : view === 'open' ? (
            "Buy a card from the dashboard to start your inventory. We'll track P&L from the live Snkrdunk price."
          ) : (
            'Sold positions land here — with realized P&L, hold time, and the signal at the time of buy.'
          )}
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <Link to="/" className="chip">
            Go to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

function HoldingsTable({
  rows,
  view,
  onSell,
}: {
  rows: Holding[];
  view: View;
  onSell: (h: Holding) => void;
}) {
  const qc = useQueryClient();
  const undoMut = useMutation({
    mutationFn: (id: string) => deleteHolding(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['holdings'] });
      qc.invalidateQueries({ queryKey: ['portfolio'] });
      qc.invalidateQueries({ queryKey: ['cards'] });
    },
    onError: (e) => {
      const msg = e instanceof HttpError ? `${e.status}` : 'network';
      alert(`Could not undo: ${msg}. Sell it instead.`);
    },
  });

  return (
    <div className="port-scroll">
      <table className="holdings-table">
        <thead>
          <tr>
            <th>Card</th>
            <th className="right">Qty</th>
            <th className="right">{view === 'closed' ? 'Bought / Sold' : 'Bought'}</th>
            <th className="right">Cost basis</th>
            <th className="right">{view === 'closed' ? 'Proceeds' : 'Now'}</th>
            <th className="right">{view === 'closed' ? 'Realized' : 'Unrealized'}</th>
            <th className="right">Held{view === 'open' && ' · Signal'}</th>
            <th className="right">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((h) => {
            const pnl = view === 'closed' ? (h.realizedHkd ?? 0) : (h.unrealizedHkd ?? 0);
            const pnlPct = view === 'closed' ? (h.realizedPct ?? 0) : (h.unrealizedPct ?? 0);
            const pos = pnl >= 0;
            const days =
              view === 'closed' ? closedDays(h.buyAt, h.sellAt ?? null) : daysHeldFor(h.buyAt);
            const now = view === 'closed' ? h.proceedsHkd : h.valueHkd;
            const canUndo = view === 'open' && undoEligible(h.createdAt);
            return (
              <tr key={h.id}>
                <td>
                  <div className="col-card">
                    <div className="mini-art">{h.card && <CardArt card={miniCard(h)} />}</div>
                    <div className="col-name-meta">
                      <Link to={`/card/${h.cardId}`} className={h.card?.jpEn ? '' : 'jp'}>
                        {h.card?.jpEn ?? h.card?.jp ?? h.cardId}
                      </Link>
                      <span className="meta">
                        {h.card?.set} · {h.card?.num} · {h.card?.rarity}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="right">
                  {h.qty > 1 ? (
                    <span className="qty-pill">×{h.qty}</span>
                  ) : (
                    <span className="num-mute">1</span>
                  )}
                </td>
                <td className="right">
                  <div className="num">{fmtDateShort(h.buyAt)}</div>
                  {view === 'closed' && h.sellAt && (
                    <div className="num-mute" style={{ fontSize: 10 }}>
                      → {fmtDateShort(h.sellAt)}
                    </div>
                  )}
                </td>
                <td className="right num">
                  HK${fmtHKD(h.buyHkd)}
                  {h.qty > 1 && (
                    <div className="num-mute" style={{ fontSize: 10 }}>
                      ×{h.qty} = {fmtHKD(h.costHkd)}
                    </div>
                  )}
                </td>
                <td className="right num">{now == null ? '—' : `HK$${fmtHKD(now)}`}</td>
                <td className={`right pnl-cell ${pos ? 'pos' : 'neg'}`}>
                  <span className="big">{fmtSignedHKD(pnl)}</span>
                  <span className="pct">{fmtPctSigned(pnlPct)}</span>
                </td>
                <td className="right num-dim">
                  {days}d
                  {view === 'open' && h.card && (
                    <span style={{ marginLeft: 8 }}>
                      <span
                        className={`sig sig-${h.card.signal}`}
                        style={{ fontSize: 9, padding: '1px 4px' }}
                      >
                        {labelForSignal(h.card.signal)}
                      </span>
                    </span>
                  )}
                </td>
                <td className="right">
                  <div className="row-actions" style={{ justifyContent: 'flex-end', gap: 6 }}>
                    {view === 'open' && (
                      <button type="button" className="row-act-btn sell" onClick={() => onSell(h)}>
                        Sell
                      </button>
                    )}
                    {canUndo && (
                      <button
                        type="button"
                        className="row-act-btn"
                        onClick={() => {
                          if (
                            confirm('Undo this buy? Use this if you entered the wrong price/date.')
                          ) {
                            undoMut.mutate(h.id);
                          }
                        }}
                        disabled={undoMut.isPending}
                        title="Within 1h of entry"
                      >
                        Undo
                      </button>
                    )}
                    <Link to={`/card/${h.cardId}`} className="row-act-btn view">
                      Open
                    </Link>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function closedDays(buyAt: string, sellAt: string | null): number {
  if (!sellAt) return 0;
  const ms = new Date(sellAt).getTime() - new Date(buyAt).getTime();
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
}

function undoEligible(createdAt: string): boolean {
  const age = Date.now() - new Date(createdAt).getTime();
  return age <= 60 * 60 * 1000;
}

/** Minimal CardSummary-shaped object that CardArt can render. */
function miniCard(h: Holding): CardSummary {
  return {
    id: h.cardId,
    productId: h.cardId,
    jp: h.card?.jp ?? '',
    jpEn: h.card?.jpEn ?? null,
    set: h.card?.set ?? '',
    num: h.card?.num ?? '',
    rarity: h.card?.rarity ?? '',
    pop: null,
    signal: h.card?.signal ?? 'HOLD',
    score: h.card?.score ?? 0,
    subs: [],
    price: null,
    d1: null,
    d7: null,
    d30: null,
    image: h.card?.image ?? null,
  };
}
